import type Phaser from 'phaser';
import { onAppUrlOpen } from '../platform/native';
import { linkExit } from './linkExit';
import { setPendingLawnCode } from './scenes/sharedLawn';

/**
 * Links into the game (docs/ONLINE.md): `?lawn=CODE` (a shared lawn), `?challenge=ID` (a
 * beat-my-time challenge), `?race=CODE` (a live race's room, docs/LIVE.md) and `?friend=CODE` (a
 * player to add as a friend: their friend code, or a player id in older links). The web game reads them from its address at boot; the apps get them
 * when a playlawnmower.com link opens the app (universal links). Either way the menu opens them.
 */

let pendingChallenge: string | null = null;
let pendingRace: string | null = null;
/** The pending race was an invite the player already said JOIN to (no second asking). */
let pendingRaceJoined = false;
let pendingFriend: string | null = null;

/**
 * What a friend link names: a secret friend code (8 characters, docs/FRIENDS.md) or, in links from
 * before friend codes, a player id (10). The friends screen tells them apart.
 */
const friendId = (url: URL): string | null => {
  const value = url.searchParams.get('friend') ?? '';
  if (/^[2-9A-HJKMNP-Z]{8}$/i.test(value)) return value.toUpperCase();
  const id = value.toLowerCase();
  return /^[0-9a-z]{10}$/.test(id) ? id : null;
};

/** A race room's code in a link, if it is one. */
const raceCode = (url: URL): string | null => {
  const code = url.searchParams.get('race')?.toUpperCase() ?? '';
  return /^[2-9A-HJKMNP-Z]{6}$/.test(code) ? code : null;
};

/** Reads (and takes out of the address bar) a challenge or race in the page's link, at boot. */
export function takeLinkedChallenge(): void {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('challenge');
    const race = raceCode(url);
    const friend = friendId(url);
    if (!id && !race && !friend) return;
    if (id) pendingChallenge = id;
    if (race) pendingRace = race;
    if (friend) pendingFriend = friend;
    url.searchParams.delete('challenge');
    url.searchParams.delete('race');
    url.searchParams.delete('friend');
    window.history.replaceState(null, '', url.toString());
  } catch {
    // No usable location (a test harness): nothing linked.
  }
}

/** The linked race room's code, once (the menu opens it), and whether JOIN was said already. */
export function pendingRaceCode(): { code: string; joined: boolean } | null {
  const code = pendingRace;
  const joined = pendingRaceJoined;
  pendingRace = null;
  pendingRaceJoined = false;
  return code ? { code, joined } : null;
}

/** The linked friend's player id, once (the menu asks to add them). */
export function pendingFriendId(): string | null {
  const id = pendingFriend;
  pendingFriend = null;
  return id;
}

/** The linked challenge, once (the menu opens it). */
export function pendingChallengeId(): string | null {
  const id = pendingChallenge;
  pendingChallenge = null;
  return id;
}

/**
 * Opens a race room from inside the game (a friend's invite, docs/FRIENDS.md): the menu takes it
 * as it would a link, leaving the screen now unless a lawn, race or match is in play.
 */
export function openRaceLink(game: Phaser.Game, code: string): void {
  pendingRace = code;
  pendingRaceJoined = true;
  const exit = linkExit(game.scene.getScenes(true).map((s) => s.scene.key));
  if (exit && exit !== 'wait') game.scene.getScene(exit.from)?.scene.start('Menu');
}

/**
 * Natively, a link that opens the app: kept for the menu, which is shown now unless a lawn, race
 * or match is in play (then it opens when the player is next on the menu; see `linkExit`).
 */
export function listenForOpenedLinks(game: Phaser.Game): void {
  onAppUrlOpen((link) => {
    let url: URL;
    try {
      url = new URL(link);
    } catch {
      return;
    }
    const lawn = url.searchParams.get('lawn');
    const challenge = url.searchParams.get('challenge');
    const race = raceCode(url);
    const friend = friendId(url);
    if (lawn) setPendingLawnCode(lawn);
    else if (challenge) pendingChallenge = challenge;
    else if (race) {
      pendingRace = race;
      pendingRaceJoined = false;
    } else if (friend) pendingFriend = friend;
    else return;
    const exit = linkExit(game.scene.getScenes(true).map((s) => s.scene.key));
    if (exit && exit !== 'wait') game.scene.getScene(exit.from)?.scene.start('Menu');
  });
}
